import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { Observable } from 'rxjs';

import {
    DepositExportData,
    DepositsExportFormatEnum,
    ProjectionEnum,
} from '@connect-services/client/services/export.service';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import {
    exportFormatsOptions,
    projectionsOptions,
    SelectComponent,
    SelectOption,
} from '@connect-services/src/lib/components/select/select.component';
import { CustomColumn, CustomTableComponent } from '@connect-services/src/lib/components/table/table.component';
import {
    Deposit,
    DepositControllerGet200Response,
    DepositControllerGetRequestParams,
    DepositFolder,
    DepositsService,
} from '@connect-services/src/sdk/api';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { FormGroupType } from '@shared/client/models/form.model';
import { defaultPaginationParameters } from '@shared/client/models/pagination.model';

export interface ExportDepositFormType extends FormGroupType {
    depositId: FormControl<string>;
    format: FormControl<DepositsExportFormatEnum>;
    grouped: FormControl<boolean>;
    projection: FormControl<ProjectionEnum>;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CheckboxComponent,
        CustomTableComponent,
        LoadingComponent,
        SelectComponent,
        TranslocoPipe,
        ReactiveFormsModule,
    ],
    selector: 'app-export-deposit-modal-second-step',
    templateUrl: './export-deposit-modal-second-step.component.html',
})
export class ExportDepositModalSecondStepComponent {
    public columns: CustomColumn[] = [
        {
            key: 'data',
            label: 'data',
            translate: true,
        },
        {
            key: 'version',
            label: 'version',
            sortable: false,
            translate: true,
        },
        {
            key: 'format',
            label: 'format',
            sortable: false,
            translate: true,
        },
        {
            key: 'projection',
            label: 'projection',
            sortable: false,
            translate: true,
        },
        {
            key: 'group',
            label: 'group',
            sortable: false,
            translate: true,
        },
    ];
    public formArray: FormArray<FormGroup<ExportDepositFormType>> = new FormArray<FormGroup<ExportDepositFormType>>([]);
    public readonly selectedDeposits = input.required<SelectOption<DepositFolder>[]>();
    public readonly tableRowsData = computed(() =>
        this.selectedDeposits().map((depositOption: SelectOption<DepositFolder>): DepositFolder => depositOption.value),
    );

    protected readonly CheckboxState = CheckboxState;
    protected readonly depositsByFolder: WritableSignal<Map<string, Deposit[]>> = signal(new Map<string, Deposit[]>());
    protected readonly exportFormatsOptions = exportFormatsOptions;
    protected readonly projectionsOptions = projectionsOptions;

    private readonly depositsService: DepositsService = inject(DepositsService);
    private readonly fetchAllService: FetchAllService = inject(FetchAllService);

    public constructor() {
        effect(async (): Promise<void> => {
            const promises: Promise<{
                deposits: Deposit[];
                folderId: string;
            }>[] = this.selectedDeposits().map((selectedDeposit: SelectOption<DepositFolder>) =>
                this.fetchAllDepositAndFolderIdBySelectedFolder(selectedDeposit),
            );

            const results: {
                deposits: Deposit[];
                folderId: string;
            }[] = await Promise.all(promises);

            this.depositsByFolder.set(new Map<string, Deposit[]>());
            for (const { folderId, deposits } of results) {
                this.depositsByFolder().set(folderId, deposits);
            }

            untracked(() =>
                this.selectedDeposits().forEach((depositFolder: SelectOption<DepositFolder>) => {
                    const group = new FormGroup<ExportDepositFormType>({
                        depositId: new FormControl<string>(
                            this.depositsByFolder().get(depositFolder.value.id)![0]?.id ?? '',
                            { nonNullable: true },
                        ),
                        format: new FormControl<DepositsExportFormatEnum>(DepositsExportFormatEnum.GEOPACKAGE, {
                            nonNullable: true,
                        }),
                        grouped: new FormControl<boolean>(false, { nonNullable: true }),
                        projection: new FormControl<ProjectionEnum>(ProjectionEnum.LAMBERT93, {
                            nonNullable: true,
                        }),
                    });
                    this.formArray.push(group);
                }),
            );
        });
    }

    protected get formatDefaultValue(): SelectOption<DepositsExportFormatEnum>[] {
        const defaultOptions = [DepositsExportFormatEnum.GEOPACKAGE];

        return this.exportFormatsOptions.filter((format: SelectOption<DepositsExportFormatEnum>) =>
            defaultOptions.includes(format.value),
        );
    }

    protected get groupAllCheckState(): CheckboxState {
        if (
            this.formArray.controls.every((formGroup: FormGroup<ExportDepositFormType>) =>
                formGroup.controls.grouped.getRawValue(),
            )
        ) {
            return CheckboxState.CHECKED;
        }
        if (
            this.formArray.controls.find((formGroup: FormGroup<ExportDepositFormType>) =>
                formGroup.controls.grouped.getRawValue(),
            )
        ) {
            return CheckboxState.INDETERMINATE;
        }

        return CheckboxState.UNCHECKED;
    }

    protected get projectionDefaultValue(): SelectOption<ProjectionEnum>[] {
        const defaultOptions = [ProjectionEnum.LAMBERT93];

        return this.projectionsOptions.filter((projection: SelectOption<ProjectionEnum>) =>
            defaultOptions.includes(projection.value),
        );
    }

    public getExportData(): DepositExportData[] {
        return this.formArray.controls.map((formGroup: FormGroup<ExportDepositFormType>): DepositExportData => ({
            depositId: formGroup.controls.depositId.value,
            format: formGroup.controls.format.value,
            grouped: formGroup.controls.grouped.value,
            projection: formGroup.controls.projection.value,
        }));
    }

    protected getDepositVersionOptions(folderId: string): SelectOption<Deposit>[] {
        if (!this.depositsByFolder().has(folderId)) {
            return [];
        }

        return this.depositsByFolder()
            .get(folderId)!
            .map((deposit: Deposit): SelectOption<Deposit> => ({
                label: deposit.version.toString(),
                value: deposit,
            }));
    }

    protected updateExportDataGroupedState(index?: number): void {
        if (index !== undefined) {
            const previousGroupedState: boolean = this.formArray.at(index).get('grouped')?.getRawValue();
            this.formArray.at(index).controls.grouped.setValue(!previousGroupedState);

            return;
        }

        const checkAll: boolean = !this.formArray.controls.every(
            (formGroup: FormGroup<ExportDepositFormType>): boolean | undefined =>
                formGroup.controls.grouped.getRawValue(),
        );

        this.formArray.controls.forEach((formGroup: FormGroup<ExportDepositFormType>) => {
            formGroup.controls.grouped.setValue(checkAll);
        });
    }

    protected updateFormat(value: DepositsExportFormatEnum[], index: number): void {
        this.formArray.at(index).controls.format.setValue(value[0]);
    }

    protected updateProjection(value: ProjectionEnum[], index: number): void {
        this.formArray.at(index).controls.projection.setValue(value[0]);
    }

    protected updateVersion(value: Deposit[], index: number): void {
        this.formArray.at(index).controls.depositId.setValue(value[0].id);
    }

    private async fetchAllDepositAndFolderIdBySelectedFolder(
        selectedDepositFolder: SelectOption<DepositFolder>,
    ): Promise<{
        deposits: Deposit[];
        folderId: string;
    }> {
        const deposits: Deposit[] = await this.fetchAllService.getAll<DepositControllerGetRequestParams, Deposit>(
            (params: DepositControllerGetRequestParams): Observable<DepositControllerGet200Response> =>
                this.depositsService.depositControllerGet({
                    ...params,
                    depositFolder: selectedDepositFolder.value.id,
                    onlyValid: true,
                }),
            { ...defaultPaginationParameters, orderBy: 'version' },
        );

        return { deposits, folderId: selectedDepositFolder.value.id };
    }
}
